/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux Imports */

import {
	setCreateNewServiceMaxPriceThunk,
	setCreateNewServiceMinPriceThunk,
	setCreateNewServiceOtherSubTypeThunk,
	setCreateNewServiceShowPriceThunk,
	setCreateNewServiceSubTypeThunk,
	setCreateNewServiceTypeThunk,
} from "../../../../../redux-magic/store";
import StateInterface from "../../../../../redux-magic/state-interface";
import { SearchFilterOptionText } from "../../../../../redux-magic/sub-interfaces/search-types/search-filter-option";

/* Component Imports */

import {
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	useTheme,
} from "@mui/material";
import router, { useRouter } from "next/router";

/* Function Imports */

import CurrencyToNumberPipe from "../../../../../lib/CurrencyToNumberPipe";
import NumberToCurrencyPipe from "../../../../../lib/NumberToCurrencyPipe";
import PreparePriceString from "../../../../../lib/PreparePriceString";

/* Styled Components */

const PaperContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	borderRadius: "8px",
	gap: "1rem",
	overflow: "auto",
	paddingTop: "1rem",
	scrollbarWidth: "none", // Hide the scrollbar for firefox
	"&::-webkit-scrollbar": {
		display: "none", // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
	},
	"&-ms-overflow-style:": {
		display: "none", // Hide the scrollbar for IE
	},
	[theme.breakpoints.down("sm")]: {
		width: "100%",
	},
}));

const TextContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	gap: "0.6rem",
	width: "100%",
	// padding: "0.25rem 0rem 0.25rem 0rem",
}));

const ShowPriceDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	width: "100%",
	color: theme.palette.text.secondary,
}));

const ServiceDetails = ({
	newServiceData,
	dispatch,
	serviceTypeError,
	setServiceTypeError,
	serviceSubTypeError,
	setServiceSubTypeError,
	serviceMinPriceError,
	setServiceMinPriceError,
	serviceMaxPriceError,
	setServiceMaxPriceError,
	serviceProviderNameError,
	setServiceProviderNameError,
	setServiceOtherSubTypeError,
	serviceOtherSubTypeError,
}: {
	newServiceData: StateInterface["new_service_data"];
	dispatch: Function;
	serviceTypeError: boolean;
	setServiceTypeError: Function;
	serviceSubTypeError: boolean;
	setServiceSubTypeError: Function;
	serviceMinPriceError: boolean;
	setServiceMinPriceError: Function;
	serviceMaxPriceError: boolean;
	setServiceMaxPriceError: Function;
	serviceProviderNameError: boolean;
	setServiceProviderNameError: Function;
	setServiceOtherSubTypeError: Function;
	serviceOtherSubTypeError: boolean;
}) => {
	const theme = useTheme();

	const router = React.useRef(useRouter()).current;

	const [errorOtherSubtype, setErrorOtherSubtype] = React.useState(serviceOtherSubTypeError);

	const [errorMinPrice, setErrorMinPrice] = React.useState<boolean>(false);

	const [errorMaxPrice, setErrorMaxPrice] = React.useState<boolean>(false);

	/* Translations */

	const localLang = {
		heading:
			router.locale === "ar-AE" ? "تفاصيل الخدمة" : router.locale === "kn-IN" ? "ಸೇವಾ ವಿವರಗಳು" : "Service details",
		serviceType: router.locale === "ar-AE" ? "نوع الخدمة" : router.locale === "kn-IN" ? "ಸೇವೆ ಪ್ರಕಾರ" : "Service type",
		serviceSubType:
			router.locale === "ar-AE"
				? "نوع الخدمة الفرعي"
				: router.locale === "kn-IN"
					? "ಸೇವೆ ಉಪ ಪ್ರಕಾರ"
					: "Service subtype",
		showPrice: router.locale === "ar-AE" ? "أدخل السعر" : router.locale === "kn-IN" ? "ಬೆಲೆ ತೋರಿಸಿ" : "Show price",
		price: router.locale === "ar-AE" ? "السعر" : router.locale === "kn-IN" ? "ಬೆಲೆ" : "Price",
		minPrice: router.locale === "ar-AE" ? "السعر الأدنى" : router.locale === "kn-IN" ? "ಕನಿಷ್ಠ ಬೆಲೆ" : "Minimum price",
		errorMinPrice:
			router.locale === "ar-AE"
				? "القيمة أكبر من السعر الأقصى"
				: router.locale === "kn-IN"
					? "ಮೂಲ್ಯ ಗರಿಷ್ಟ ಬೆಲೆಯಿಂದ ಹೆಚ್ಚಾಗಿದೆ."
					: "Value is more than max price",
		maxPrice: router.locale === "ar-AE" ? "السعر الأقصى" : router.locale === "kn-IN" ? "ಗರಿಷ್ಟ ಬೆಲೆ" : "Maximum price",
		errorMaxPrice:
			router.locale === "ar-AE"
				? "القيمة أقل من السعر الأدنى"
				: router.locale === "kn-IN"
					? "ಮೂಲ್ಯ ಕನಿಷ್ಠ ಬೆಲೆಯಿಂದ ಕಡಿಮೆಯಾಗಿದೆ."
					: "Value is less than min price",
		construction: router.locale === "ar-AE" ? "البناء" : router.locale === "kn-IN" ? "ನಿರ್ಮಾಣ" : "Construction",
		interior_design:
			router.locale === "ar-AE" ? "تصميم داخلي" : router.locale === "kn-IN" ? "ಆಂತರಿಕ ವಿನ್ಯಾಸ" : "Interior design",
		property_management:
			router.locale === "ar-AE"
				? "إدارة الممتلكات"
				: router.locale === "kn-IN"
					? "ಆಸ್ತಿ ನಿರ್ವಹಣೆ"
					: "Property management",
		packers_and_movers:
			router.locale === "ar-AE"
				? "نقل الأثاث"
				: router.locale === "kn-IN"
					? "ಪ್ಯಾಕರ್ ಮತ್ತು ಮುವರ್"
					: "Packers and movers",
		legal_and_finance:
			router.locale === "ar-AE"
				? "قانوني ومالي"
				: router.locale === "kn-IN"
					? "ಕಾನೂನು ಮತ್ತು ಹಣಕಾಸು"
					: "Legal and finance",
		photography_and_videography:
			router.locale === "ar-AE"
				? "تصوير فوتوغرافي وفيديو"
				: router.locale === "kn-IN"
					? "ಫೋಟೋಗ್ರಫಿ ಮತ್ತು ವಿಡಿಯೋ"
					: "Photography and videography",
		civil_contractor:
			router.locale === "ar-AE" ? "مقاول مدني" : router.locale === "kn-IN" ? "ನಾಗರಿಕ ಒಪ್ಪಂದದಾರ" : "Civil contractor",
		architecture:
			router.locale === "ar-AE" ? "هندسة معمارية" : router.locale === "kn-IN" ? "ವಾಸ್ತುಶಿಲ್ಪ" : "Architecture",
		masonry_and_concrete:
			router.locale === "ar-AE"
				? "الملاط والخرسانة"
				: router.locale === "kn-IN"
					? "ಮಾಸನ್ ಮತ್ತು ಕಾನ್‌ಕ್ರೀಟ್"
					: "Masonry and concrete",
		earth_moving: router.locale === "ar-AE" ? "تحريك الأرض" : router.locale === "kn-IN" ? "ಭೂಗತ ಚಲನೆ" : "Earth moving",
		demolition: router.locale === "ar-AE" ? "هدم" : router.locale === "kn-IN" ? "ವಿನಾಶ" : "Demolition",
		fabrication: router.locale === "ar-AE" ? "تصنيع" : router.locale === "kn-IN" ? "ನಿರ್ಮಾಣ" : "Fabrication",
		hvac_installation:
			router.locale === "ar-AE"
				? "تركيب التدفئة والتهوية وتكييف الهواء"
				: router.locale === "kn-IN"
					? "ಎಚ್‌ವಿಎಸಿ ಸ್ಥಾಪನೆ"
					: "HVAC installation",
		mep_consultant:
			router.locale === "ar-AE" ? "استشاري ميب" : router.locale === "kn-IN" ? "ಎಮ್‌ಇಪಿ ಸಲಹೆಗಾರ" : "MEP consultant",
		rainwater_harvesting:
			router.locale === "ar-AE"
				? "حصاد مياه الأمطار"
				: router.locale === "kn-IN"
					? "ಮಳೆ ನೀರಿನ ಸಂಗ್ರಹಣೆ"
					: "Rainwater harvesting",
		structural_engineering:
			router.locale === "ar-AE"
				? "الهندسة الإنشائية"
				: router.locale === "kn-IN"
					? "ಸಂರಚನಾ ಇಂಜಿನಿಯರಿಂಗ್"
					: "Structural engineering",
		pool_consultants:
			router.locale === "ar-AE" ? "استشاريين مسبح" : router.locale === "kn-IN" ? "ಪೂಲ್ ಸಲಹೆಗಾರರು" : "Pool consultants",
		sewage_treatment_plant:
			router.locale === "ar-AE"
				? "محطة معالجة مياه الصرف الصحي"
				: router.locale === "kn-IN"
					? "ಮಲಬದ್ಧ ನೀರಿನ ಶುದ್ಧೀಕರಣ ಘಟಕ"
					: "Sewage treatment plant",
		lift_installation:
			router.locale === "ar-AE" ? "تركيب المصعد" : router.locale === "kn-IN" ? "ಲಿಫ್ಟ್ ಸ್ಥಾಪನೆ" : "Lift installation",
		cctv: router.locale === "ar-AE" ? "كاميرات المراقبة" : router.locale === "kn-IN" ? "ಸಿಸಿಟಿವಿ" : "CCTV",
		legal_consultation:
			router.locale === "ar-AE" ? "استشارة قانونية" : router.locale === "kn-IN" ? "ಕಾನೂನು ಸಲಹೆ" : "Legal consultation",
		documentation: router.locale === "ar-AE" ? "توثيق" : router.locale === "kn-IN" ? "ದಾಖಲೆ" : "Documentation",
		home_loan: router.locale === "ar-AE" ? "قرض عقاري" : router.locale === "kn-IN" ? "ಮನೆ ಸಾಲ" : "Home loan",
		investment_advisory:
			router.locale === "ar-AE"
				? "استشارات استثمارية"
				: router.locale === "kn-IN"
					? "ಹೂಡಿಕೆ ಸಲಹೆ"
					: "Investment advisory",
		liaisoning: router.locale === "ar-AE" ? "التواصل" : router.locale === "kn-IN" ? "ಲಯಿಸನಿಂಗ್" : "Liaisoning",
	};

	/* Functions */

	const subtype_options: Array<SearchFilterOptionText> = [];

	const construction_options: Array<SearchFilterOptionText> = [
		{
			id: "civil_contractor",
			title: localLang.civil_contractor,
			value: "civil_contractor",
		},
		{
			id: "architecture",
			title: localLang.architecture,
			value: "architecture",
		},
		{
			id: "masonry_and_concrete",
			title: localLang.masonry_and_concrete,
			value: "masonry_and_concrete",
		},
		{
			id: "earth_moving",
			title: localLang.earth_moving,
			value: "earth_moving",
		},
		{
			id: "demolition",
			title: localLang.demolition,
			value: "demolition",
		},
		{
			id: "fabrication",
			title: localLang.fabrication,
			value: "fabrication",
		},
		{
			id: "hvac_installation",
			title: localLang.hvac_installation,
			value: "hvac_installation",
		},
		{
			id: "mep_consultant",
			title: localLang.mep_consultant,
			value: "mep_consultant",
		},
		{
			id: "rainwater_harvesting",
			title: localLang.rainwater_harvesting,
			value: "rainwater_harvesting",
		},
		{
			id: "structural_engineering",
			title: localLang.structural_engineering,
			value: "structural_engineering",
		},
		{
			id: "pool_consultants",
			title: localLang.pool_consultants,
			value: "pool_consultants",
		},
		{
			id: "sewage_treatment_plant",
			title: localLang.sewage_treatment_plant,
			value: "sewage_treatment_plant",
		},
		{
			id: "lift_installation",
			title: localLang.lift_installation,
			value: "lift_installation",
		},
		{
			id: "cctv",
			title: localLang.cctv,
			value: "cctv",
		},
	];

	const legal_and_finance_options: Array<SearchFilterOptionText> = [
		{
			id: "legal_consultation",
			title: localLang.legal_consultation,
			value: "legal_consultation",
		},
		{
			id: "documentation",
			title: localLang.documentation,
			value: "documentation",
		},
		{
			id: "home_loan",
			title: localLang.home_loan,
			value: "home_loan",
		},
		{
			id: "investment_advisory",
			title: localLang.investment_advisory,
			value: "investment_advisory",
		},
		{
			id: "liaisoning",
			title: localLang.liaisoning,
			value: "liaisoning",
		},
	];

	/* Service subtype options */

	switch (newServiceData.service_type[0]) {
		case "construction":
			subtype_options.push(...construction_options);
			break;
		case "legal_and_finance":
			subtype_options.push(...legal_and_finance_options);
			break;
		default:
			subtype_options.push();
			break;
	}

	return (
		<PaperContainer id="service-details">
			<TextContainer>
				{/* <TextField
					value={newServiceData.service_provider.name}
					sx={{
						width: "100%",
						"& .MuiOutlinedInput-root": {
							borderRadius: "0.75rem",
						},
					}}
					onChange={(event: any) => {
						dispatch(setCreateNewServiceProviderNameThunk(event.target.value));
						setServiceProviderNameError(false);
					}}
					label={router.locale === "kn-IN" ? "ಸೇವಾ ಒದಗಿಸುವವರ ಹೆಸರು*" : "Service provider name*"}
					autoComplete="off"
					error={serviceProviderNameError}
					helperText={serviceProviderNameError ? "Please enter a service provider name" : ""}
				/> */}
				<FormControl
					sx={{
						width: "100%",
						margin: "0.5rem 0rem 0rem 0rem",
						[theme.breakpoints.down("sm")]: {
							margin: "0.5rem 0rem 0rem 0rem",
						},
					}}
					error={serviceTypeError}
				>
					<InputLabel id="demo-simple-select-helper-label">
						{router.locale === "kn-IN" ? "ಸೇವೆಯ ಪ್ರಕಾರ*" : "Service type*"}
					</InputLabel>
					<Select
						labelId="demo-simple-select-helper-label"
						id="demo-simple-select-helper"
						value={newServiceData.service_type}
						sx={{
							width: "100%",
							height: "3.5rem",
							[theme.breakpoints.down("sm")]: {
								width: "100%",
							},
						}}
						label="Service type*"
						onChange={(event: any) => {
							const selectedValue =
								typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value;
							dispatch(setCreateNewServiceTypeThunk(selectedValue));
							if (subtype_options.length > 0) {
								dispatch(setCreateNewServiceSubTypeThunk([event.target.value]));
							} else {
								dispatch(setCreateNewServiceSubTypeThunk([]));
							}
							dispatch(setCreateNewServiceOtherSubTypeThunk(""));
							dispatch(setCreateNewServiceMinPriceThunk(0));
							dispatch(setCreateNewServiceMaxPriceThunk(0));
							setServiceTypeError(false);
						}}
					>
						<MenuItem value="construction">{localLang.construction}</MenuItem>
						<MenuItem value="interior_design">{localLang.interior_design}</MenuItem>
						<MenuItem value="property_management">{localLang.property_management}</MenuItem>
						<MenuItem value="packers_and_movers">{localLang.packers_and_movers}</MenuItem>
						<MenuItem value="legal_and_finance">{localLang.legal_and_finance}</MenuItem>
						<MenuItem value="photography_and_videography">{localLang.photography_and_videography}</MenuItem>
					</Select>
					<FormHelperText>{serviceTypeError ? "Please select a service type" : ""}</FormHelperText>
				</FormControl>

				{/* Service Subtype */}

				{newServiceData.service_type.length > 0 && subtype_options.length > 0 ? (
					<TextContainer>
						<TextContainer
							sx={{
								width: "100%",
								justifyContent: "space-between",
								alignItems: "center",
								margin: "0.5rem 0rem 0rem 0rem",
								[theme.breakpoints.down("sm")]: {
									flexDirection: "column",
									gap: "1rem",
								},
							}}
						>
							<FormControl
								sx={{
									width: "100%",
									[theme.breakpoints.down("sm")]: {
										width: "100%",
									},
								}}
								error={serviceSubTypeError}
							>
								<InputLabel id="demo-simple-select-helper-label">{localLang.serviceSubType + "*"}</InputLabel>
								<Select
									labelId="demo-simple-select-helper-label"
									id="demo-simple-select-helper"
									value={newServiceData.service_subtype}
									sx={{
										width: "100%",
										height: "3.5rem",
										[theme.breakpoints.down("sm")]: {
											width: "100%",
										},
									}}
									label={localLang.serviceSubType + "*"}
									onChange={(event: any) => {
										dispatch(
											setCreateNewServiceSubTypeThunk(
												typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value,
											),
										);
										setServiceSubTypeError(false);
									}}
								>
									{subtype_options?.map((subtype: any, index: number) => {
										return (
											<MenuItem
												key={index}
												value={subtype.value}
											>
												{subtype.title}
											</MenuItem>
										);
									})}
								</Select>
								<FormHelperText>{serviceSubTypeError ? "Please select a service subtype" : ""}</FormHelperText>
							</FormControl>

							{/* {newServiceData.service_subtype.includes("other") ? (
								<TextField
									value={newServiceData.other_service_subtype}
									sx={{
										width: "100%",
										"& .MuiOutlinedInput-root": {
											borderRadius: "0.75rem",
										},
									}}
									onChange={(event: any) => {
										dispatch(setCreateNewServiceOtherSubTypeThunk(event.target.value));
										if (newServiceData.service_subtype.includes("other") && event.target.value === "") {
											setErrorOtherSubtype(true);
										} else {
											setErrorOtherSubtype(false);
										}
									}}
									label={router.locale === "kn-IN" ? "ಇತರೆ ಸೇವೆಯ ಉಪಪ್ರಕಾರ*" : "Other service subtype*"}
									placeholder={router.locale === "kn-IN" ? "ಸೇವೆಯ ಉಪಪ್ರಕಾರವನ್ನು ನಮೂದಿಸಿ" : "Enter service subtype"}
									autoComplete="off"
									error={errorOtherSubtype}
									helperText={errorOtherSubtype ? "Please select a service subtype" : ""}
								/>
							) : null} */}
						</TextContainer>
					</TextContainer>
				) : null}

				{/* Show Price */}

				{newServiceData.service_type.length !== 0 && newServiceData.service_subtype.length !== 0 ? (
					<TextContainer>
						<FormGroup>
							<FormControlLabel
								control={
									<Checkbox
										sx={{ margin: "0rem 0.25rem 0rem 0rem" }}
										checked={newServiceData.price.show_price}
										onChange={() => dispatch(setCreateNewServiceShowPriceThunk(!newServiceData.price.show_price))}
									/>
								}
								label={localLang.showPrice}
							/>
						</FormGroup>
					</TextContainer>
				) : null}

				{/* Price */}

				{newServiceData.service_type.length !== 0 &&
				newServiceData.service_subtype.length !== 0 &&
				newServiceData.price.show_price ? (
					<TextContainer>
						<ShowPriceDiv>
							{newServiceData.price.min === newServiceData.price.max
								? "₹ " + PreparePriceString(newServiceData.price.max)
								: "₹ " +
									PreparePriceString(newServiceData.price.min) +
									" - " +
									PreparePriceString(newServiceData.price.max)}
						</ShowPriceDiv>

						<TextContainer
							sx={{
								display: "flex",
								flexDirection: "row",
								width: "100%",
								justifyContent: "space-between",
								alignItems: "flex-start",
								margin: "0.5rem 0rem 0rem 0rem",
								[theme.breakpoints.down("sm")]: {
									gap: "0.5rem",
								},
							}}
						>
							<TextField
								value={newServiceData.price.min === 0 ? "" : NumberToCurrencyPipe(newServiceData.price.min)}
								slotProps={{
									htmlInput: {
										min: 0,
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								sx={{
									width: "50%",
									// [theme.breakpoints.down("sm")]: {
									// 	width: "100%",
									// },
									"& .MuiOutlinedInput-root": {
										borderRadius: "0.75rem",
									},
								}}
								onChange={(event: any) => {
									dispatch(setCreateNewServiceMinPriceThunk(CurrencyToNumberPipe(event.target.value)));
									if (
										(newServiceData.price.max !== 0 &&
											CurrencyToNumberPipe(event.target.value) > newServiceData.price.max) ||
										isNaN(CurrencyToNumberPipe(event.target.value))
									) {
										setErrorMinPrice(true);
										setErrorMaxPrice(true);
									} else {
										setErrorMinPrice(false);
										setErrorMaxPrice(false);
										setServiceMinPriceError(false);
									}
								}}
								label={newServiceData.price.show_price ? localLang.minPrice + "*" : localLang.minPrice}
								error={errorMinPrice || serviceMinPriceError}
								helperText={
									errorMinPrice || serviceMinPriceError
										? localLang.errorMinPrice
										: newServiceData.price.min > 0
											? PreparePriceString(newServiceData.price.min)
											: ""
								}
								autoComplete="off"
							/>

							<TextField
								value={newServiceData.price.max === 0 ? "" : NumberToCurrencyPipe(newServiceData.price.max)}
								slotProps={{
									htmlInput: {
										min: 0,
										inputMode: "numeric",
										pattern: "[0-9]*",
									},
								}}
								onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
									if (event.key === "Enter" || event.key === "Next") {
										(event.target as HTMLInputElement).blur();
									}
								}}
								autoComplete="off"
								sx={{
									width: "50%",
									// [theme.breakpoints.down("sm")]: {
									// 	width: "100%",
									// },
									"& .MuiOutlinedInput-root": {
										borderRadius: "0.75rem",
									},
								}}
								onChange={(event: any) => {
									dispatch(setCreateNewServiceMaxPriceThunk(CurrencyToNumberPipe(event.target.value)));
									if (
										(newServiceData.price.min !== 0 &&
											CurrencyToNumberPipe(event.target.value) < newServiceData.price.min) ||
										isNaN(CurrencyToNumberPipe(event.target.value))
									) {
										setErrorMinPrice(true);
										setErrorMaxPrice(true);
									} else {
										setErrorMinPrice(false);
										setErrorMaxPrice(false);
										setServiceMaxPriceError(false);
									}
								}}
								label={newServiceData.price.show_price ? localLang.maxPrice + "*" : localLang.maxPrice}
								error={errorMaxPrice || serviceMaxPriceError}
								helperText={
									errorMaxPrice || serviceMaxPriceError
										? localLang.errorMaxPrice
										: newServiceData.price.max > 0
											? PreparePriceString(newServiceData.price.max)
											: ""
								}
							/>
						</TextContainer>
					</TextContainer>
				) : null}
			</TextContainer>
		</PaperContainer>
	);
};

export default ServiceDetails;
